import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../hooks/AuthProvider";
import { FORMS_FORM_TEST_SEND } from "../../../config/urls";
import { grayFontColor } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";

const digitsOf = (value) => value.replace(/\D/g, "");

/**
 * Sends the form to a phone number to try it out. Drafts go out in Meta's draft
 * mode, published forms live. The number must have messaged the business in the
 * last 24 hours; the backend says so when it hasn't.
 */
const TestSendDialog = ({ open, formId, published, onClose }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;

  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setSending(false);
    }
  }, [open]);

  const valid = digitsOf(phone).length >= 8;

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const token = await tokenRef.current();
      const response = await fetch(FORMS_FORM_TEST_SEND(formId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify({ phone: digitsOf(phone) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setResult({
          ok: false,
          message: data?.error || "Couldn't send the test",
          issues: data?.issues || [],
        });
        return;
      }
      setResult({ ok: true, mode: data.mode });
    } catch (error) {
      setResult({ ok: false, message: "Couldn't reach the server. Try again.", issues: [] });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={typography.h5}>Send a test</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {published
              ? "Sends the published form to this number."
              : "Sends this draft in test mode. Replies are saved as test submissions."}{" "}
            The number must have messaged you in the last 24 hours.
          </Typography>
          <TextField
            label="WhatsApp number"
            placeholder="919876543210"
            size="small"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value.slice(0, 20))}
            onKeyDown={(e) => e.key === "Enter" && valid && !sending && send()}
            inputProps={{ inputMode: "tel", "aria-label": "WhatsApp number" }}
            helperText="Include the country code, without + or spaces."
          />
          {result?.ok && (
            <Alert severity="success" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
              Test sent{result.mode === "draft" ? " in draft mode" : ""}. Open WhatsApp on that
              number and tap the button on the message.
            </Alert>
          )}
          {result && !result.ok && (
            <Alert severity="error" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
              {result.message}
              {result.issues.length > 0 && (
                <Box component="ul" sx={{ m: "6px 0 0", pl: 2.25 }}>
                  {result.issues.slice(0, 5).map((issue, index) => (
                    <li key={index}>{issue.message}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ButtonTwo title="Close" onClick={onClose} disabled={sending} />
        <ButtonOne
          title={sending ? "Sending…" : "Send test"}
          onClick={send}
          disabled={!valid || sending}
          startIcon={sending ? <CircularProgress size={18} /> : undefined}
        />
      </DialogActions>
    </Dialog>
  );
};

export default TestSendDialog;
